import _ from 'underscore';
import { v4 } from 'uuid';

import { projectStateAdapter } from '../react/state/project-state/project-state-adapter';
import { userStateAdapter } from '../react/state/user-state/user-state-adapter';
import { ThemeManager } from '../react/themes/theme-manager';
import { TokenService } from '../services/authentication/token.service';
import {
  CerbosApiService,
  CerbosPrincipalAttributes,
  CerbosProjectRole,
  CerbosRequestData,
  CerbosViewScope,
} from '../services/cerbos/cerbos-api';
import { cerbosControlledFeatures, CerbosFeature } from '../services/cerbos/features';
import { cerbosControlledViews, CerbosView } from '../services/cerbos/views';
import { licensePermissionChecks } from '../utils/permission-check/license';
import { isDefined } from '../utils/variables';
import { CompanyStore } from './company.store';

export enum CerbosPermissionStatus {
  ACCEPT,
  DENY,
  // This is temporary while we transition to Cerbos system,
  // and returns if the current resource is not implemented yet,
  NOT_DEFINED,
}

interface CerbosAttributeProject {
  id: string;
  companyId: string;
}

export class PermissionStore {
  private static _instance = new PermissionStore();
  static get instance() {
    return PermissionStore._instance;
  }

  private cerbosApiService = new CerbosApiService();

  private allowedAccessKeys = new Set<string>();

  private currentPermissionProjectId?: string;

  requirePermissionsReload(projectId?: string) {
    return this.currentPermissionProjectId !== projectId || this.allowedAccessKeys.size <= 0;
  }

  async refreshAccessState(projectId?: string) {
    const resourceProject = projectStateAdapter.getSnapshot().project;
    const company = CompanyStore.instance
      .getCompanies()
      ?.find((company) =>
        company.Projects.find((project) => project.id === (projectId ?? resourceProject?.id)),
      );

    const tokenService = new TokenService();
    const token = tokenService.get();

    const principalAttributes = this.resolveCerbosPrincipalAttributes();

    const accessResponse = await Promise.all([
      this.executeCerbosAccessRequest({
        requestId: v4(),
        principal: { attributes: principalAttributes },
        resource: {
          attributes: {
            projectId: resourceProject?.id ?? projectId,
            companyId: company?.id ?? resourceProject?.companyId,
            isProjectShared: ThemeManager.instance.isSharedView,
          },
          kind: 'view',
          scope: this.resolveViewScope(),
        },
        actions: Array.from(cerbosControlledViews),
        jwtToken: token,
      } as CerbosRequestData),
      await this.executeCerbosAccessRequest({
        requestId: v4(),
        principal: { attributes: principalAttributes },
        resource: {
          attributes: {
            projectId: resourceProject?.id ?? projectId,
            companyId: company?.id ?? resourceProject?.companyId,
            isProjectShared: ThemeManager.instance.isSharedView,
          },
          kind: 'feature',
          scope: this.resolveViewScope(),
        },
        actions: Array.from(cerbosControlledFeatures),
        jwtToken: token,
      } as CerbosRequestData),
    ]);

    this.currentPermissionProjectId = resourceProject?.id ?? projectId;

    this.allowedAccessKeys = new Set(
      _.flatten(accessResponse.map((response) => response.allowedActions())),
    );

    return accessResponse;
  }

  async executeCerbosAccessRequest(requestData: CerbosRequestData) {
    return await this.cerbosApiService.checkAccess(requestData);
  }

  checkAccessToView(view?: CerbosView) {
    if (!isDefined(view) || !cerbosControlledViews.has(view)) {
      return CerbosPermissionStatus.NOT_DEFINED;
    }
    return this.allowedAccessKeys.has(view)
      ? CerbosPermissionStatus.ACCEPT
      : CerbosPermissionStatus.DENY;
  }

  checkAccessToFeature(feature?: CerbosFeature) {
    if (!isDefined(feature) || !cerbosControlledFeatures.has(feature)) {
      return CerbosPermissionStatus.NOT_DEFINED;
    }
    return this.allowedAccessKeys.has(feature)
      ? CerbosPermissionStatus.ACCEPT
      : CerbosPermissionStatus.DENY;
  }

  // Fetches all of the user and company projects,
  // and adds them to the Cerbos policy check.
  resolveCerbosPrincipalAttributes(): CerbosPrincipalAttributes {
    const companyProjects = _.flatten(
      CompanyStore.instance.getCompanies()?.map((company) => company.Projects) ?? [],
    );
    const snapshot = userStateAdapter.getSnapshot();
    const licenses = snapshot.licenses;

    return {
      projects: this.resolveProjects(
        companyProjects as CerbosAttributeProject[],
        (snapshot.getProjects() ?? []) as CerbosAttributeProject[],
      ),
      companyUsers: snapshot.getCompanyUsers() ?? [],
      accountKeys: [],
      hasDesignSeat: snapshot.hasValidDesignToolSeat(),
      licenses: licenses.map((license) => ({
        companyId: license.companyId,
        type: license.licenseType,
        active:
          license.licenseDemoPeriods?.length === 0 ||
          (license.licenseDemoPeriods?.some(licensePermissionChecks.isDemoPeriodValid) ?? true),
      })),
    };
  }

  private resolveProjects(
    companyProjects: CerbosAttributeProject[],
    userProjects: CerbosAttributeProject[],
  ) {
    // Remove any user projects if the user also is a company admin for that project.
    const filteredUserProjects = userProjects.filter(
      (project) => !_.find(companyProjects, { id: project.id }),
    );

    const buildProjectAttributes = (project: CerbosAttributeProject, role: CerbosProjectRole) => ({
      id: project.id,
      role,
      userTags: [],
    });

    return [
      ...companyProjects.map((project) => buildProjectAttributes(project, 'admin')),
      ...filteredUserProjects.map((project) => buildProjectAttributes(project, 'user')),
    ];
  }

  private resolveViewScope(): CerbosViewScope | undefined {
    const theme = ThemeManager.instance.theme;
    switch (theme.themeName) {
      case 'moba':
        return 'moba';
      case 'default':
      case 'makin':
        return 'skymap';
      default:
        return undefined;
    }
  }
}
